import React, { useState } from 'react';
import { ProgressBar } from 'primereact/progressbar';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Tooltip } from 'primereact/tooltip';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';

const SitemapGenerator = () => {
    const SITE_URL = process.env.REACT_APP_BORDERGARITA_PUBLIC_APP_URL;
    const SECRET_TOKEN = process.env.REACT_APP_BORDERGARITA_REVALIDATE_SECRET;

    const [loading, setLoading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [generated, setGenerated] = useState(false);
    const [sitemapUrl] = useState(`${SITE_URL}/sitemap.xml`);
    const [error, setError] = useState(null);
    const toast = React.useRef(null);


    const simulateProgress = () => {
        setProgress(0);
        const interval = setInterval(() => {
            setProgress(prev => {
                if (prev >= 100) {
                    clearInterval(interval);
                    return 100;
                }
                return prev + 10;
            });
        }, 300);
    };

    const generateSitemap = async () => {
        setLoading(true);
        setError(null);
        setGenerated(false);
        simulateProgress();

        try {
            const response = await fetch(`${SITE_URL}/api/revalidate-sitemap?secret=${SECRET_TOKEN}`, {
                method: 'POST'
            });

            if (!response.ok) {
                throw new Error(`Error: ${response.status} - ${response.statusText}`);
            }

            //   const data = await response.json();

            // Simulate completion after API response
            setTimeout(() => {
                setProgress(100);
                setGenerated(true);
                // setSitemapUrl(`${SITE_URL}/sitemap.xml`);

                if (toast.current) {
                    toast.current.show({
                        severity: 'success',
                        summary: 'Success!',
                        detail: 'Sitemap regenerated successfully',
                        life: 3000
                    });
                }

                setLoading(false);
            }, 1000);

        } catch (err) {
            console.error('Error generating sitemap:', err);
            setError(err.message);
            setLoading(false);
            setProgress(0);

            if (toast.current) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Could not regenerate the sitemap',
                    life: 5000
                });
            }
        }
    };

    const confirmRegenerate = () => {
        confirmDialog({
            message: 'Are you sure you want to regenerate the sitemap? This may temporarily affect SEO.',
            header: 'Confirm Regeneration',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-primary',
            acceptLabel: 'Yes, regenerate',
            accept: () => generateSitemap(),
            reject: () => { }
        });
    };

    const openSitemap = () => {
        window.open(sitemapUrl, '_blank');
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(sitemapUrl);
        if (toast.current) {
            toast.current.show({
                severity: 'info',
                summary: 'Copied',
                detail: 'URL copied to clipboard',
                life: 2000
            });
        }
    };

    const header = (
        <div className="flex flex-column align-items-center mt-6">
            <div className="relative">
                <i className="pi pi-sitemap text-6xl text-blue-500 mb-3" style={{ fontSize: '4rem' }}></i>
            </div>
            <h1 className="text-4xl font-bold text-gray-800 mb-2 text-center">
                Sitemap Generator
            </h1>
            <p className="text-lg text-gray-600 text-center mt-2">
                Regenerate your sitemap.xml to improve your site's SEO.
                <br />
                According to all new pages recently added.
            </p>
        </div>
    );

    return (
        <div className="flex justify-content-center bg-gray-100 p-3">
            <Toast ref={toast} position="top-right" />
            <ConfirmDialog />

            <Card header={header} className="w-full md:w-10 lg:w-8 xl:w-6 shadow-5 border-round-xl">
                <div className="text-center mb-5">
                    <div className="mb-6">
                        {/* <h3 className="text-2xl font-semibold text-gray-800 mb-3">Estado Actual del Sitemap</h3> */}

                        {loading ? (
                            <div className="border-round overflow-hidden mb-3">
                                <ProgressBar value={progress} className="h-1rem" showValue={false}></ProgressBar>
                                <div className="flex justify-content-between mt-2">
                                    <span className="text-sm text-gray-600">Generating...</span>
                                    <span className="text-sm font-medium text-blue-600">{progress}%</span>
                                </div>
                            </div>
                        ) : generated ? (
                            <div className="bg-green-100 p-3 border-round border-1 border-green-300 mb-3">
                                <div className="flex align-items-center justify-content-center">
                                    <i className="pi pi-check-circle text-green-500 mr-2"></i>
                                    <span className="text-green-700 font-medium">Sitemap generated successfully</span>
                                </div>
                            </div>
                        ) : error ? (
                            <div className="bg-red-100 p-3 border-round border-1 border-red-300 mb-3">
                                <div className="flex align-items-center justify-content-center">
                                    <i className="pi pi-exclamation-triangle text-red-500 mr-2"></i>
                                    <span className="text-red-700 font-medium">Error: {error}</span>
                                </div>
                            </div>
                        ) : <></>}

                        <div className="flex justify-content-center gap-3 mt-4">
                            <Button
                                label="Regenerate sitemap"
                                icon={generated ? "pi pi-refresh" : "pi pi-play"}
                                className="p-button-primary p-button-raised"
                                onClick={confirmRegenerate}
                                loading={loading}
                                disabled={loading}
                            />

                            {generated && (
                                <>
                                    <Button
                                        label="Ver Sitemap"
                                        icon="pi pi-external-link"
                                        className="p-button-success"
                                        onClick={openSitemap}
                                    />
                                    <Tooltip target=".copy-btn" />
                                    <Button
                                        icon="pi pi-copy"
                                        className="p-button-help copy-btn"
                                        onClick={copyToClipboard}
                                        data-pr-tooltip="Copiar URL"
                                        data-pr-position="top"
                                    />
                                </>
                            )}
                        </div>

                        <div className="mt-4 p-3 bg-gray-100 border-round">
                            <div className="flex align-items-center justify-content-between">
                                <span className="text-gray-700 font-medium">Sitemap URL:</span>
                                <span className="text-blue-500 font-medium">
                                    <a href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                                        {sitemapUrl}
                                    </a>
                                </span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-6 p-3 surface-100 border-round">
                        <p className="text-gray-700 m-0 text-center">
                            <i className="pi pi-info-circle text-blue-500 mr-2"></i>
                            Regenerating the sitemap may take several minutes. Please do not close this page during the process.
                        </p>
                    </div>
                </div>
            </Card>

            <style jsx>{`
        .generated-badge {
          position: absolute;
          top: -5px;
          right: -5px;
          background: #22c55e;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          border: 2px solid white;
        }
        
        :global(.p-timeline-event-opposite) {
          flex: 0;
        }
        
        :global(.p-timeline-event-content) {
          padding: 0.5rem 0;
        }
      `}</style>
        </div>
    );
};

export default SitemapGenerator;